import {
  ConditionalRender,
  DeleteIconFilled,
  Modal,
  TableSkeleton,
} from '@medeloop/medeloop-components-library';
import ShareModal from '@medeloop/medeloop-components-library/dist/components/ShareModal/ShareModal';
import {
  AccessType,
  InvitableUser,
  InvitedEntities,
  InvitedUser,
} from '@medeloop/medeloop-components-library/dist/components/ShareModal/types';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetCohorts,
  useGetCohortsByDataSourceId,
  useUpdateCohortMutation,
} from 'src/api/cohorts-queries';
import { getCohortResearchers } from 'src/api/data-sources';
import { getOrganizationResearchers } from 'src/api/organizations';
import { addUserToCohort, removeUserFromCohort } from 'src/api/users';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { showToast } from 'src/store/toast';
import { Cohort } from 'src/types/cohorts';
import { DataSourceResearcher, UserDataSourceRole } from 'src/types/data-sources';
import { CohortTable } from './cohort-table';

export const DatasetCohort = () => {
  const { datasetId } = useParams();

  const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [invitableResearchers, setInvitableResearchers] = useState<InvitableUser[]>([]);
  const [invitedResearchers, setInvitedResearchers] = useState<InvitedUser[]>([]);
  const [fetchingShareResearchersData, setFetchingShareResearchersData] = useState(false);
  const [isArchiveCohortModalOpen, setIsArchiveCohortModalOpen] = useState(false);
  const [isDeleteCohortModalOpen, setIsDeleteCohortModalOpen] = useState(false);

  const navigate = useNavigate();
  const account = useSelector(selectAccount);
  const token = useSelector(selectAuthToken);
  const dispatch = useDispatch();

  const updateCohortMutation = useUpdateCohortMutation();

  const { isLoading, data } = datasetId
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useGetCohortsByDataSourceId(datasetId ?? '')
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useGetCohorts();

  const fetchInvitableResearchers = async () => {
    const researchersMap = new Map<string, InvitableUser>();

    const resolvedResearchers = await Promise.all(
      account.organizationAdminIds.map((organizationId) =>
        getOrganizationResearchers({ token, organizationId }),
      ),
    );

    resolvedResearchers.forEach((researchers) => {
      researchers.researchers.forEach((researcher) => {
        if (!researchersMap.has(researcher.userId) && researcher.userId !== account.userId) {
          researchersMap.set(researcher.userId, {
            id: researcher.userId,
            email: researcher.researcherEmail,
            name: `${researcher.researcherGivenName} ${researcher.researcherFamilyName}`,
          });
        }
      });
    });

    setInvitableResearchers(Array.from(researchersMap.values()));
  };

  const fetchInvitedResearchers = async (dataSourceId: string, cohortId: string) => {
    const invitedResearchers = await getCohortResearchers({
      token,
      dataSourceId,
      cohortId,
    });

    const invitedEntities =
      invitedResearchers?.researchers.map((researcher: DataSourceResearcher) => ({
        id: researcher.userId,
        email: researcher.researcherEmail,
        name: `${researcher.researcherGivenName} ${researcher.researcherFamilyName}`,
        accessType: 'VIEW' as AccessType,
      })) ?? [];
    setInvitedResearchers(invitedEntities);
  };

  const onShareButtonClick = async (cohort: Cohort) => {
    setFetchingShareResearchersData(true);
    setSelectedCohort(cohort);
    setIsShareModalOpen(true);
    await fetchInvitedResearchers(cohort.dataSourceId, cohort.cohortId);
    await fetchInvitableResearchers();
    setFetchingShareResearchersData(false);
  };

  const onDeleteButtonClick = (cohort: Cohort) => {
    setSelectedCohort(cohort);
    setIsDeleteCohortModalOpen(true);
  };

  const addResearchersToCohort = async (
    users: InvitedUser[],
    dataSourceId: string,
    cohortId: string,
  ) => {
    return await Promise.all(
      users.map((user) => {
        addUserToCohort({
          token,
          userId: user.id,
          dataSourceId,
          cohortId,
          role: UserDataSourceRole.RESEARCHER,
        });
      }),
    );
  };

  const removeResearchersFromCohort = async (
    users: InvitedUser[],
    dataSourceId: string,
    cohortId: string,
  ) => {
    return await Promise.all(
      users.map((user) => {
        removeUserFromCohort({
          token,
          userId: user.id,
          dataSourceId,
          cohortId,
          role: UserDataSourceRole.RESEARCHER,
        });
      }),
    );
  };

  const resetShareModalState = () => {
    setInvitedResearchers([]);
    setInvitableResearchers([]);
    setIsShareModalOpen(false);
    setSelectedCohort(null);
  };

  const onShareSave = (recipients: InvitedEntities) => {
    if (!selectedCohort?.dataSourceId) {
      resetShareModalState();
      return;
    }

    const invitedUsers = invitedResearchers.map((user) => user.id);

    const newUsers = recipients.users.filter((user) => !invitedUsers.includes(user.id));

    const removedUsers = invitedResearchers.filter(
      (user) => !recipients.users.some((newUser) => newUser.id === user.id),
    );

    try {
      if (newUsers.length > 0) {
        addResearchersToCohort(newUsers, selectedCohort.dataSourceId, selectedCohort.cohortId);
      }
      if (removedUsers.length > 0) {
        removeResearchersFromCohort(
          removedUsers,
          selectedCohort.dataSourceId,
          selectedCohort.cohortId,
        );
      }
      dispatch(
        showToast({
          type: 'success',
          message: 'Cohort updated successfully',
        }),
      );
    } catch (error) {
      console.error('Error updating cohort researchers:', error);
    }
    resetShareModalState();
  };

  const onOpenChange = (open: boolean) => {
    setIsShareModalOpen(open);
  };

  const cohorts = useMemo(() => {
    if (!data?.data?.cohorts) return [];
    return data.data.cohorts;
  }, [data]);

  const handleViewCohort = (cohort: Cohort) => {
    navigate(`/account/data/${cohort.dataSourceId}/cohort/${cohort.cohortId}`);
  };

  const handleArchiveCohort = () => {
    if (selectedCohort && !selectedCohort.archivedAt) {
      updateCohortMutation.mutate({
        cohortId: selectedCohort.cohortId,
        dataSourceId: selectedCohort.dataSourceId,
        archivedAt: new Date().toISOString(),
      });
    }
  };

  const handleDeleteCohort = () => {
    if (selectedCohort) {
      updateCohortMutation.mutate({
        cohortId: selectedCohort.cohortId,
        dataSourceId: selectedCohort.dataSourceId,
        deletedAt: new Date().toISOString(),
      });
    }
  };

  const onArchiveUnarchiveClick = (cohort: Cohort) => {
    if (cohort.archivedAt) {
      updateCohortMutation.mutate({
        cohortId: cohort.cohortId,
        dataSourceId: cohort.dataSourceId,
        archivedAt: null,
      });
    } else {
      setSelectedCohort(cohort);
      setIsArchiveCohortModalOpen(true);
    }
  };

  return (
    <div className="flex flex-col gap-6 mt-6">
      <ConditionalRender assertion={!isLoading} fallback={<TableSkeleton />}>
        <CohortTable
          cohorts={cohorts}
          isLoading={isLoading}
          onViewCohort={handleViewCohort}
          onShareCohort={onShareButtonClick}
          onDeleteCohort={onDeleteButtonClick}
          onArchiveUnArchiveCohort={onArchiveUnarchiveClick}
        />
      </ConditionalRender>

      {isShareModalOpen && selectedCohort && (
        <ShareModal
          title={selectedCohort.name}
          isLoading={fetchingShareResearchersData}
          showCopyLink={false}
          showGeneralAccess={false}
          invitableEntities={{ users: invitableResearchers, organizations: [] }}
          existingInvites={{
            users: invitedResearchers,
            organizations: [],
            external: [],
          }}
          maxInvitableEmails={100}
          onOpenChange={onOpenChange}
          onCancel={() => resetShareModalState()}
          onSave={(recipients) => onShareSave(recipients as InvitedEntities)}
          translations={{
            cancel: 'Cancel',
            share: 'Share',
            save: 'Save',
            copyLink: 'Copy link',
            whoHasAccess: 'Who has access?',
            emailPlaceholder: 'Enter an email address, then press ENTER',
            emailTeamPlaceholder: 'Enter a team name or email address, then press ENTER',
            new: 'New',
            remove: 'Remove',
            canView: 'Can view',
            generalAccess: 'General access',
            everyoneIn: 'Everyone in',
            onlyInvitedPeople: 'Only invited people',
            unsavedChanges: 'Unsaved changes',
            discardChanges: 'Discard changes',
            unsafeChangesWarning:
              'You have unsaved changes. Are you sure you want to discard them and exit?',
            copiedToClipboard: 'Copied to clipboard',
            shareWithExternal: 'Share with external emails?',
            proceedWithSharing: 'Proceed with sharing',
            emailsExternalToOrganization: 'The following emails are external to your organization',
            carefulSharingInfoWarning:
              'Please be cautious when sharing to ensure the security of your information.',
          }}
        />
      )}

      {isArchiveCohortModalOpen && selectedCohort && (
        <Modal
          actionType="primary"
          actionText="Archive cohort"
          actionFunction={() => handleArchiveCohort()}
          isOpen={isArchiveCohortModalOpen}
          onOpenChange={() => {
            setSelectedCohort(null);
            setIsArchiveCohortModalOpen(false);
          }}
          title="Archive cohort"
        >
          <div className="flex flex-col gap-6 w-96">
            <div>
              Archiving this cohort will temporarily disable access to its data for analysis and
              reporting. <br /> You can unarchive it at any time.
            </div>
            <div>Are you sure you want to archive {selectedCohort.name}?</div>
          </div>
        </Modal>
      )}

      {isDeleteCohortModalOpen && selectedCohort && (
        <Modal
          actionType="danger"
          actionText="Delete permanently"
          actionFunction={() => handleDeleteCohort()}
          isOpen={isDeleteCohortModalOpen}
          onOpenChange={() => {
            setSelectedCohort(null);
            setIsDeleteCohortModalOpen(false);
          }}
          title="Delete cohort"
          titleIcon={
            <div className="w-16 h-16 bg-white rounded-[32px] justify-center items-center inline-flex">
              <DeleteIconFilled className="text-red-500" />
            </div>
          }
        >
          <div className="flex flex-col gap-6 w-96">
            <span>
              This cohort is currently paused and no longer active. Deleting it will permanently
              remove all its configuration and history.
            </span>
            <span>This action cannot be undone.</span>
            <span>Are you sure you want to permanently delete {selectedCohort.name}?</span>
          </div>
        </Modal>
      )}
    </div>
  );
};
